import { isValidCents } from './cents.js'
import type { Cents } from './cents.js'

/**
 * Rateio que **fecha**: `sumCents(allocate(total, pesos)) === total`, sempre.
 *
 * O resto vai para o **último** item, de propósito. Não é o rateio mais justo —
 * é o mais auditável: um lugar previsível é mais fácil de conferir do que um
 * lugar esperto, e o que importa aqui é a soma fechar. Decisão registrada em
 * `Docs/03-Arquitetura/Dinheiro, datas e fuso.md`.
 *
 * `Math.trunc` e não `Math.floor`: truncar arredonda em direção ao zero, então
 * um total negativo produz partes negativas com a mesma forma do positivo
 * (`-100` em três vira `[-33, -33, -34]`). Com `floor`, viraria
 * `[-34, -34, -32]` — o resto mudaria de lugar quando o sinal mudasse.
 */
export function allocate(totalCents: Cents, weights: number[]): Cents[] {
  if (!isValidCents(totalCents)) {
    throw new Error(`allocate: total precisa ser inteiro em centavos, recebi ${String(totalCents)}`)
  }
  if (weights.length === 0) {
    throw new Error('allocate: lista de pesos vazia — não há para onde ratear')
  }
  for (const weight of weights) {
    if (!Number.isFinite(weight) || weight < 0) {
      throw new Error(`allocate: peso precisa ser finito e não-negativo, recebi ${String(weight)}`)
    }
  }

  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0)
  if (totalWeight === 0) {
    throw new Error('allocate: soma dos pesos é zero — o rateio não teria como fechar')
  }

  // Todas as partes menos a última saem da proporção truncada; a última é o que
  // sobra. É isso que faz a soma fechar sem depender de arredondamento nenhum.
  const parts: Cents[] = []
  let allocated = 0
  for (const weight of weights.slice(0, -1)) {
    const part = Math.trunc((totalCents * weight) / totalWeight)
    // `totalCents * weight` estoura para `Infinity` antes da divisão quando o
    // peso é absurdo (~1.8e299 para um total de R$ 10 milhões), e daí sai
    // `[Infinity, -Infinity]`: a soma vira `NaN` e o "sempre fecha" desta
    // função deixa de valer em silêncio. Inalcançável pela entrada de uma casa
    // — mas o contrato aqui em cima é incondicional, então ou ele ganha a
    // ressalva ou a função recusa. Recusar é mais barato de confiar.
    if (!isValidCents(part)) {
      throw new Error(
        `allocate: peso ${String(weight)} produz parte fora da faixa segura de centavos`,
      )
    }
    parts.push(part)
    allocated += part
  }
  parts.push(totalCents - allocated)

  return parts
}
